/**
 * Attribution capture.
 *
 * Runs on EVERY page view (see <AnalyticsTracker />). Ad params, click IDs and
 * cookies are merged into a single record that is persisted in localStorage for
 * 90 days (sessionStorage as a fallback), so a visitor can land on an ad page,
 * browse five other pages — or come back tomorrow — and the quote form still
 * carries the original campaign data.
 *
 * First-touch fields are written once and never overwritten. Last-touch fields
 * are refreshed whenever the visitor arrives with new campaign params.
 */

/** Fields that identify a NEW paid/campaign touch when present in the URL. */
const TOUCH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "gclsrc",
  "msclkid",
  "fbclid",
  "ttclid",
  "li_fat_id",
  "epik",
  "irclid",
  "keyword",
  "matchtype",
  "device",
  "network",
  "campaignid",
  "adgroupid",
  "creative",
  "placement",
  "adposition",
  "loc_physical_ms",
  "loc_interest_ms",
  "campaign_id",
  "adset_id",
  "ad_id",
  "ref",
] as const;

/** Everything stored/sent, in the order it is emitted as hidden inputs. */
const KEYS = [
  ...TOUCH_KEYS,
  "fbp",
  "fbc",
  "ga_client_id",
  "source",
  "landing_page",
  "referrer",
  "first_landing_page",
  "first_referrer",
  "first_touch_at",
  "last_touch_at",
  "page_count",
  "pages_viewed",
  "user_agent",
  "screen_size",
  "timezone",
] as const;

export type TrackingParams = Record<(typeof KEYS)[number], string>;

const STORE_KEY = "cp_tracking_v2";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const EMPTY = (): TrackingParams =>
  KEYS.reduce((acc, k) => ({ ...acc, [k]: "" }), {} as TrackingParams);

function readCookie(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]!) : "";
}

type Stored = { at: number; data: Partial<TrackingParams> };

function readStored(): Partial<TrackingParams> {
  for (const store of [safeLocal(), safeSession()]) {
    if (!store) continue;
    try {
      const raw = store.getItem(STORE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Stored | Partial<TrackingParams>;
      if (parsed && typeof parsed === "object" && "data" in parsed) {
        const s = parsed as Stored;
        if (Date.now() - (s.at || 0) > MAX_AGE_MS) continue;
        return s.data || {};
      }
      return (parsed as Partial<TrackingParams>) || {};
    } catch {
      /* ignore corrupt entry */
    }
  }
  return {};
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function persist(data: TrackingParams) {
  const payload = JSON.stringify({ at: Date.now(), data } satisfies Stored);
  for (const store of [safeLocal(), safeSession()]) {
    try {
      store?.setItem(STORE_KEY, payload);
    } catch {
      /* storage blocked */
    }
  }
}

/**
 * Reads URL params + cookies, merges with anything stored from earlier page
 * views, persists the result and returns it. Safe to call on every navigation.
 */
export function captureTrackingParams(): TrackingParams {
  if (typeof window === "undefined") return EMPTY();

  const stored = readStored();
  const url = new URLSearchParams(window.location.search);
  const out = EMPTY();

  // A fresh campaign touch resets the campaign fields; otherwise carry forward.
  const isNewTouch = TOUCH_KEYS.some((k) => !!url.get(k));

  for (const key of KEYS) {
    const fromUrl = (url.get(key) || "").toString();
    const prev = (stored[key] || "").toString();
    const isTouchKey = (TOUCH_KEYS as readonly string[]).includes(key);
    out[key] = (fromUrl || (isTouchKey && isNewTouch ? "" : prev)).slice(0, 500);
  }

  // Common alternate param spellings from ad platforms.
  out.utm_term = out.utm_term || (url.get("keyword") || "").slice(0, 500);
  out.campaignid = out.campaignid || out.campaign_id;

  // Cookies — always prefer the live value.
  out.fbp = readCookie("_fbp") || out.fbp;
  out.fbc = readCookie("_fbc") || out.fbc;
  if (!out.fbc && out.fbclid) out.fbc = `fb.1.${Date.now()}.${out.fbclid}`;
  const ga = readCookie("_ga"); // GA1.1.1234567890.1712345678
  if (ga) {
    const parts = ga.split(".");
    out.ga_client_id = parts.length >= 4 ? parts.slice(-2).join(".") : ga;
  } else {
    out.ga_client_id = out.ga_client_id || stored.ga_client_id || "";
  }

  // Human-readable Source so GHL always has something usable.
  if (isNewTouch || !stored.source) {
    if (out.utm_source) out.source = out.utm_source;
    else if (out.gclid || out.gbraid || out.wbraid || out.gad_source) out.source = "Google Ads";
    else if (out.fbclid) out.source = "Meta Ads";
    else if (out.msclkid) out.source = "Microsoft Ads";
    else if (out.ttclid) out.source = "TikTok Ads";
    else if (!out.source) {
      const ref = document.referrer || "";
      if (!ref) out.source = "Direct";
      else if (/google\./i.test(ref)) out.source = "Google Organic";
      else if (/bing\.|duckduckgo|yahoo\./i.test(ref)) out.source = "Search Engine";
      else if (/facebook|instagram|fb\.com/i.test(ref)) out.source = "Facebook / Instagram";
      else {
        try {
          out.source = new URL(ref).hostname.replace(/^www\./, "");
        } catch {
          out.source = "Referral";
        }
      }
    }
  } else {
    out.source = stored.source || out.source;
  }

  const here = window.location.href.slice(0, 500);
  out.landing_page = isNewTouch || !stored.landing_page ? here : stored.landing_page!;
  out.referrer =
    isNewTouch || !stored.referrer ? (document.referrer || "").slice(0, 500) : stored.referrer!;

  // First touch — written once, never overwritten.
  out.first_landing_page = stored.first_landing_page || here;
  out.first_referrer = stored.first_referrer || (document.referrer || "").slice(0, 500);
  out.first_touch_at = stored.first_touch_at || new Date().toISOString();
  out.last_touch_at = isNewTouch
    ? new Date().toISOString()
    : stored.last_touch_at || new Date().toISOString();

  // Journey — how many pages they viewed before converting, and which ones.
  const prevCount = parseInt(stored.page_count || "0", 10) || 0;
  out.page_count = String(prevCount + 1);
  const path = window.location.pathname;
  const seen = (stored.pages_viewed || "").split(" > ").filter(Boolean);
  if (seen[seen.length - 1] !== path) seen.push(path);
  out.pages_viewed = seen.slice(-15).join(" > ").slice(0, 500);

  // Device context — useful for lead quality reporting in GHL.
  out.user_agent = navigator.userAgent.slice(0, 500);
  out.screen_size = `${window.screen?.width || 0}x${window.screen?.height || 0}`;
  try {
    out.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    out.timezone = "";
  }

  persist(out);
  return out;
}

export const TRACKING_KEYS = KEYS as unknown as (keyof TrackingParams)[];
